import Parse from 'parse/dist/parse.min.js';
import {environment} from "@environments/environment";
import {Router} from "@angular/router";
import {Utils} from "../../utils";
import {Account} from "../../models/account";
import {ReplaySubject} from "rxjs";
import {Role} from "../../models/role";
import {Company} from "../../models/company";
import {RestApiManager} from "../rest-api-manager";
import {Injectable} from "@angular/core";
import {IAccountManagerService} from "./iaccount-manager-service";
import {DEBUG, DebugService} from "../debug.service";
import {BaseAccountManagerService} from "./base-account-manager-service";

@Injectable({
  providedIn: 'root'
})
export class ParseAccountManagerService extends BaseAccountManagerService implements IAccountManagerService {

  account: Account
  onLoginStatusChanged: ReplaySubject<boolean>;
  getRoles: (id: string) => Promise<Role[]>;
  isAdminOrExpert: () => boolean;

  private parseInitialized: boolean;

  constructor(
    private router: Router,
    private restApiManager: RestApiManager,
    private debugService: DebugService,
  ) {

    super(router);

    // Init Parse if required
    if (!this.parseInitialized) {
      this.initParse();
    }

  }

  private initParse() {
    Parse.initialize(environment.parse.appId, environment.parse.javascriptKey);
    Parse.serverURL = environment.parse.serverURL;
    Parse.liveQueryServerURL = environment.parse.liveQueryServerURL;
    this.parseInitialized = true;
  }

  async login(email: string, password: string): Promise<boolean> {

    let user: Parse.User;
    let authToken: string;

    try {

      user = new Parse.User();
      user.setUsername(email);
      user.setPassword(password);
      user = await user.logIn();
      authToken = user.get('sessionToken');

    } catch (e) {
      return false;
    }

    if (await this.postLogin(user, authToken)) {
      await this.router.navigate(['/']);
      return true;
    } else {
      return false;
    }

  }

  ssoLogin(accessToken: string, refreshToken: string): Promise<boolean> {
    throw new Error("SSO login is not implemented");
  }

  async loginAnonymous(firstName?: string, lastName?: string): Promise<boolean> {

    let user = Parse.User.current();

    // If signed in as expert -> logout
    if (user !== null && !Object.keys(user.get('authData')).includes('anonymous')) {
      this.debugService.log(DEBUG.LOGIN, 'Signing out as expert...');
      user.logOut();
    }

    // Anonymous login if not logged in
    if (user === null) {
      this.debugService.log(DEBUG.LOGIN, 'Signing in as anonymous...');
      user = new Parse.User();
      const authData = {
        authData: {
          id: Utils.randomString(20),
          firstName,
          lastName
        }
      };
      await user._linkWith("anonymous", authData);
    }

    // Account setup
    this.account.authProviderId = user.id;
    this.account.authToken = user.get('sessionToken');
    this.account.firstName = firstName;
    this.account.lastName = lastName;

    this.onLoginStatusChanged.next(true);
    return true;

  }

  loginWithCustomToken(token: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  async postLogin(user: Parse.User, authToken: string): Promise<boolean> {

    if (Utils.isEmpty(user.id) || Utils.isEmpty(authToken)) {
      return false;
    }

    this.account.authProviderId = user.id;
    this.account.authToken = authToken;

    // Get user account
    const account = await this.restApiManager.get<Account>(
      Account,
      `${environment.restApi.endpoint}/users/uid/${this.account.authProviderId}`,
      this.account.authToken
    );

    // Set account properties
    this.account.id = account.id;
    this.account.firstName = account.firstName;
    this.account.lastName = account.lastName;
    this.account.isAnonymous = await this.isAnonymous();

    // Get company
    this.account.company = await this.restApiManager.get<Company>(
      Company,
      `${environment.restApi.endpoint}/companies/${account.company.id}`,
      this.account.authToken
    );

    // Get roles
    this.account.roles = await this.getRoles(account.id);

    // Logout if not expert
    if (!this.isAdminOrExpert()) {
      return false;
    }

    const accountResult = this.account.validate();

    // TODO implement token refresh for Parse

    this.onLoginStatusChanged.next(accountResult);

    return accountResult;

  }

  async isLoggedIn(): Promise<boolean> {
    const user = Parse.User.current();
    const isLoggedIn = user !== null;
    if (!isLoggedIn || !(await this.postLogin(user, user.get('sessionToken')))) {
      await this.router.navigate(['login']);
    }
    return isLoggedIn;
  }

  async requestPIN(): Promise<string> {
    return (await this.restApiManager.post<{ pin: string }>(
      `${environment.restApi.endpoint}/auth/pin`,
      { },
      this.account.authToken
    )).pin;
  }

  isAnonymous(): boolean {
    try {
      return Object.keys(Parse.User.current().get('authData')).includes('anonymous');
    } catch (ex) {
      return false;
    }
  }

  async logout() {
    this.goToLoginPage();
    this.account.clear();
    this.onLoginStatusChanged.next(false);
    Parse.User.logOut();
  }

}
